import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExplorerHeader, MapPreview, TimelinePreview } from "@components";
import { fetchCatalog, fetchEvents, formatIsoDate } from "../lib/explorer";
import type { BlockingEvent, DataScope, ExplorerCatalog } from "../types/explorer";

export const Data = () => {
  const [searchParams] = useSearchParams();
  const requestedDate = searchParams.get("date");
  const [catalog, setCatalog] = useState<ExplorerCatalog | null>(null);
  const [events, setEvents] = useState<BlockingEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [scopeFilter, setScopeFilter] = useState<"all" | DataScope>("all");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchCatalog(), fetchEvents()])
      .then(([nextCatalog, nextEvents]) => {
        if (cancelled) return;
        setCatalog(nextCatalog);
        setEvents(nextEvents);
        const linked =
          requestedDate && nextCatalog.dates.some((item) => item.date === requestedDate)
            ? requestedDate
            : "";
        setSelectedDate((current) => current || linked || nextCatalog.dates[0]?.date || "");
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [requestedDate]);

  const generatedLabel = catalog
    ? formatIsoDate(catalog.generatedAt.slice(0, 10))
    : null;

  return (
    <div className="mx-auto max-w-6xl overflow-x-hidden px-4 py-8 sm:py-10">
      <ExplorerHeader
        generatedLabel={generatedLabel}
        modelVersion={catalog?.modelVersion}
      />

      {loadError ? (
        <p className="mt-8 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
          {loadError}. Run{" "}
          <code className="font-mono">uv run python generate_export.py</code>{" "}
          from <code className="font-mono">scripts/</code> to build the explorer
          files.
        </p>
      ) : null}

      {catalog && selectedDate ? (
        <>
          <MapPreview
            dates={catalog.dates}
            events={events}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            scopeFilter={scopeFilter}
            onScopeFilterChange={setScopeFilter}
          />
          <TimelinePreview
            events={events}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />
          <section className="mb-8 mt-12 panel px-4 py-6 text-center sm:px-5">
            <p className="text-sm text-muted">
              Definitions for the map layers, indices, and thesis metrics live on
              the Science page.
            </p>
            <div className="mt-4">
              <Link to="/science" className="btn-primary w-full sm:w-auto">
                Understand the science
              </Link>
            </div>
          </section>
        </>
      ) : !loadError ? (
        <p className="mt-8 text-sm text-muted">Loading explorer data…</p>
      ) : null}
    </div>
  );
};
